/* WI-12031 PR 5 — side drawer that lists every invoice a subscription line has
 * ever appeared on, including cancellation history. Reads from
 * GET /subscription-lines/{id}/invoice-history (PR 2 backend).
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { X, ExternalLink, Receipt } from 'lucide-react'

import { api } from '@/lib/api/client'
import { Drawer } from '@/components/primitives'
import type { SubscriptionLine, SubscriptionLineInvoiceHistoryRow } from '@/types/api'


function fmtMoney(n: number, currency: string): string {
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(n) + ' ' + currency
}

function fmtDate(s: string): string {
  return new Date(s + 'T00:00:00Z').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function invoiceStatusTone(status: string): { color: string; label: string } {
  // Maps the core.invoices.status CHECK enum (V059) to a colour.
  const map: Record<string, { color: string; label: string }> = {
    draft:                 { color: 'var(--ns-text-muted)',  label: 'Draft'                 },
    ready:                 { color: 'var(--ns-brand-strong)', label: 'Ready to push'         },
    pushing:               { color: 'var(--ns-brand-strong)', label: 'Pushing to Fortnox…'  },
    pushed:                { color: 'var(--ns-ok)',           label: 'Pushed'                },
    push_failed:           { color: 'var(--ns-err)',          label: 'Push failed'           },
    cancelled_in_fortnox:  { color: 'var(--ns-err)',          label: 'Cancelled in Fortnox'  },
    superseded:            { color: 'var(--ns-warn)',         label: 'Superseded'            },
    discarded:             { color: 'var(--ns-text-muted)',   label: 'Discarded'             },
  }
  return map[status] ?? { color: 'var(--ns-text-muted)', label: status }
}


export function LineInvoiceHistoryDrawer({
  line, currency, onClose,
}: {
  line: SubscriptionLine
  currency: string
  onClose: () => void
}) {
  const { data: rows = [], isLoading } = useQuery<SubscriptionLineInvoiceHistoryRow[]>({
    queryKey: ['line-invoice-history', line.id],
    queryFn:  () => api.get(`/subscription-lines/${line.id}/invoice-history`),
  })

  const activeRows   = rows.filter(r => r.unlinkedAt == null)
  const unlinkedRows = rows.filter(r => r.unlinkedAt != null)

  // Totals across active links only — unlinked rows represent cancelled
  // invoices, so summing them would double-count revenue that's been backed
  // out and re-billed on a successor.
  const totalBilled  = activeRows.reduce((s, r) => s + r.lineTotal, 0)
  const totalDays    = activeRows.reduce((s, r) => s + r.billedDays, 0)

  return (
    <Drawer open onClose={onClose}>
      <div className="drawer-head">
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Invoice history</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {line.analyzedCompanyName} · <span className="mono">{line.articleNumber}</span>
            {line.productType && <> · <strong>{line.productType}</strong></>}
          </div>
        </div>
        <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      <div className="drawer-body">
        {/* Summary band — always rendered so the user gets a quick read even
            when no invoices exist yet. */}
        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
          <SummaryCard label="Invoices"      value={String(activeRows.length)}                         />
          <SummaryCard label="Total billed"  value={fmtMoney(totalBilled, currency)}                   />
          <SummaryCard label="Total days"    value={String(totalDays)}                                  />
        </div>

        {isLoading && <div className="muted" style={{ textAlign: 'center', padding: 24 }}>Loading…</div>}

        {!isLoading && rows.length === 0 && (
          <div className="card" style={{ padding: 24, textAlign: 'center' }}>
            <Receipt size={26} className="muted" style={{ margin: '0 auto 8px' }} />
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Not yet invoiced</div>
            <div className="muted" style={{ fontSize: 13 }}>
              This line hasn't appeared on any draft invoice. Use <strong>Generate invoice</strong>
              {' '}on the subscription page to produce the first one.
            </div>
          </div>
        )}

        {!isLoading && activeRows.length > 0 && (
          <Section title={`Active links (${activeRows.length})`}>
            {activeRows.map(r => <HistoryRow key={r.junctionId} row={r} currency={currency} />)}
          </Section>
        )}

        {!isLoading && unlinkedRows.length > 0 && (
          <Section title={`Cancellation history (${unlinkedRows.length})`} muted>
            {unlinkedRows.map(r => <HistoryRow key={r.junctionId} row={r} currency={currency} unlinked />)}
          </Section>
        )}
      </div>
    </Drawer>
  )
}


function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat" style={{ padding: '10px 12px' }}>
      <div className="stat-label" style={{ fontSize: 11 }}>{label}</div>
      <div className="stat-value tabular" style={{ fontSize: 18 }}>{value}</div>
    </div>
  )
}


function Section({ title, muted, children }: { title: string; muted?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="muted" style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
        marginBottom: 6, opacity: muted ? 0.7 : 1,
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  )
}


function HistoryRow({
  row, currency, unlinked,
}: { row: SubscriptionLineInvoiceHistoryRow; currency: string; unlinked?: boolean }) {
  const tone = invoiceStatusTone(row.invoiceStatus)
  const navigate = useNavigate()

  return (
    <div className="card" style={{
      padding: 12,
      opacity: unlinked ? 0.7 : 1,
      borderLeft: unlinked ? '3px solid var(--ns-text-muted)' : `3px solid ${tone.color}`,
    }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span className="mono" style={{ fontWeight: 700, fontSize: 12.5 }}>{row.draftNumber}</span>
            {row.fortnoxInvoiceNumber && (
              <span className="muted mono" style={{ fontSize: 11 }}>
                → Fortnox #{row.fortnoxInvoiceNumber}
              </span>
            )}
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: tone.color,
              padding: '1px 7px', borderRadius: 999,
              background: `color-mix(in oklch, ${tone.color} 14%, transparent)`,
            }}>
              {tone.label}
            </span>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Period {fmtDate(row.periodOverlapStart)} → {fmtDate(row.periodOverlapEnd)} · {row.billedDays} day{row.billedDays === 1 ? '' : 's'}
          </div>
          {unlinked && row.unlinkReason && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4, fontStyle: 'italic' }}>
              Unlinked: {row.unlinkReason.replace(/_/g, ' ')}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div className="tabular" style={{ fontWeight: 700 }}>
            {fmtMoney(row.lineTotal, currency)}
          </div>
          <div className="muted tabular" style={{ fontSize: 11 }}>
            pre-disc {fmtMoney(row.preDiscountAmount, currency)}
          </div>
        </div>
      </div>
      <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
        {/* Navigate via useNavigate — composer doesn't yet read invoiceId
            from search params (a follow-up WI will wire that deep-link), but
            this still gets the user to the right module in-app without a
            full page reload. */}
        <button
          className="btn btn-sm"
          onClick={() => navigate({ to: '/invoicing/composer' })}
        >
          Open invoice <ExternalLink size={11} />
        </button>
      </div>
    </div>
  )
}
